import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FormInstance } from 'components';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { useLeadByIdQuery, useUpdateLeadMutation, useLeadSourceTypesQuery, useContactPersonReadQuery } from 'api';
import { ILeadDetailsUpdate } from './index';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import { PERMISSIONS } from 'constants/permissions';

export const useLeadDetailsEdit = (props: { form: FormInstance } & ILeadDetailsUpdate) => {
  const { onCancel, form, open } = props;
  const { hasAccess } = useAccessControl();
  const { id } = useParams() as { id: string };
  const { data: leadDetails = {} } = useLeadByIdQuery({ id });
  const { data: sourceTypes = [] } = useLeadSourceTypesQuery();
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(false);
  const [openClientModal, setOpenClientModal] = useState(false);
  const { handleSuccess, handleError } = useNotificationMessages();
  const [selectedClient, setSelectedClient] = useState<string | undefined>(leadDetails.client.id);
  const { data: contactPersons = [], isLoading: isLoadingContactPerson } = useContactPersonReadQuery(
    { entityId: selectedClient || '' },
    { skip: !selectedClient }
  );

  const [updateLeadDetails, { isLoading: detailsUpdateIsLoading }] = useUpdateLeadMutation();

  const onCancelModal = useCallback(() => {
    form.resetFields();
    setIsSaveBtnDisabled(false);
    onCancel();
  }, [form, onCancel]);

  const initialValues = useMemo(() => {
    return {
      clientId: hasAccess(PERMISSIONS.clients.client.read) ? leadDetails.client?.id : undefined,
      contactPersonId: leadDetails.contactPerson?.id,
      clientRequestNumber: leadDetails.clientRequestNumber,
      source: leadDetails.source,
    };
  }, [leadDetails, hasAccess]);

  useEffect(() => {
    if (open) {
      form.setFieldsValue(initialValues);
    }
  }, [open, form, initialValues]);

  const checkErrors = useCallback(async (values: Record<string, any>) => {
    if (values.clientId) {
      setSelectedClient(values.clientId);
    }
  }, []);

  const onFinish = useCallback(async () => {
    try {
      const payload = form.getFieldsValue();
      payload.id = id;
      const { data, error }: any = await updateLeadDetails(payload);
      if (data?.success) {
        handleSuccess('The lead details were successfully updated.');
        onCancelModal();
      } else {
        handleError(error);
      }
    } catch (err) {
      console.log(err, 'error');
      handleError(err);
    }
  }, [updateLeadDetails, handleSuccess, handleError, onCancelModal, id, form]);

  return {
    onFinish,
    checkErrors,
    isSaveBtnDisabled,
    onCancelModal,
    isLoading: detailsUpdateIsLoading,
    sourceTypes,
    openClientModal,
    setOpenClientModal,
    contactPersons,
    isLoadingContactPerson,
    selectedClient,
  };
};
